import { MappedEntity } from '@api';
import {
  SkeletonLoader,
  SkeletonLoaderProps,
} from '@design/components/skeleton-loader/SkeletonLoader';
import { WithNAFallback } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { getPriceStatus, isNonZeroReturn } from '@helpers';
import { Identifier } from '@terminal/hooks/use-live-price';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { PriceCellTooltip } from '../price-cell/price-cell-tooltip/PriceCellTooltip';
import { getPriceStamp } from '../price-display-view/helpers/price-display-helpers';
import styles from './OneDayReturnCell.module.scss';

const skeletonLoaderAreas: SkeletonLoaderProps['areas'] = [
  {
    styles: {
      width: '40px',
      height: '20px',
      borderRadius: '8px',
    },
  },
];

interface OneDayReturnCellProps {
  asset?: MappedEntity;
  isDisabled?: boolean;
  showLiveIndicator?: boolean;
  showPercentageSuffix?: boolean;
}

export const OneDayReturnCell = ({
  asset,
  isDisabled = false,
  showLiveIndicator,
  showPercentageSuffix = false,
}: OneDayReturnCellProps) => {
  const { t } = useTranslation(['widget', 'common', 'watchlistManager']);
  const {
    oneDayLiveReturn,
    isLive,
    isLivePriceSupported,
    date,
    isSubscribing,
    isExtendedHours,
  } = useOneDayReturnLivePrice(asset, Identifier.Watchlist);

  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled,
  });

  const status = getPriceStatus(oneDayLiveReturn);

  if (isSubscribing) {
    return (
      <div className={styles.skeletonLoaderWrapper}>
        <SkeletonLoader areas={skeletonLoaderAreas} />
      </div>
    );
  }

  const priceStatusClass = isDisabled ? styles.disabled : styles[status];

  return (
    <WithNAFallback
      check={
        isExtendedHours
          ? isNonZeroReturn(oneDayLiveReturn)
          : oneDayLiveReturn !== undefined
      }
      tooltip={t('widget:watchlist.noLiveDataAvailable')}
      value={
        oneDayLiveReturn !== undefined && (
          <Tooltip
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
            label={
              date && <PriceCellTooltip type={stamp.stampType} date={date} />
            }
            inPortal
            disabled={!date}
          >
            <div className={styles.flexRowWrapper}>
              <div className={priceStatusClass}>
                {formatPercentageNumber({
                  value: oneDayLiveReturn,
                  includePercentSign: showPercentageSuffix,
                  addPlus: true,
                })}
              </div>
              {showLiveIndicator && (
                <div
                  className={clsx(
                    styles.indicator,
                    isLive ? styles.indicatorLivePrice : styles.indicatorDefault
                  )}
                />
              )}
            </div>
          </Tooltip>
        )
      }
    />
  );
};
